// Route: /materials/:materialId/notes — список конспектов конкретного материала.
// Генерация — через GenerateNoteDialog (уровень + пожелания + подтверждение).

import {
	Badge,
	Button,
	CategoryTile,
	cn,
	ElevatedPanel,
	FORM_PAGE_SHELL,
	PANEL_PAD,
	SectionHeader,
	Skeleton,
} from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconChevronLeft,
	IconChevronRight,
	IconLoader2,
	IconNotebook,
	IconNotes,
	IconSparkles,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { prefetchAndSeedNoteGetCache } from "@/components/notes/_api";
import { GenerateNoteDialog } from "@/components/notes/editor/generate-dialog";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useGenerationWait } from "@/hooks/use-generation-wait";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useMaterialNotesGeneration } from "@/hooks/use-material-notes-generation";

export const Route = createFileRoute("/materials/$materialId_/notes")({
	component: MaterialNotesPage,
	pendingComponent: PagePendingFallback,
});

function MaterialNotesPage() {
	const params = useParams({ strict: false }) as { materialId?: string };
	const materialId = params.materialId;
	const { t } = useTranslation("notes");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [pendingIds, setPendingIds] = useState<string[]>([]);

	useArtifactWatching(materialId ? markArtifactSeenKey("material", materialId) : null);

	const { items, isLoading, readyNotes, generatingCount, invalidateRemote } =
		useMaterialNotesGeneration(materialId ?? null, pendingIds);
	const itemsRef = useRef(items);
	itemsRef.current = items;
	const readyNotesRef = useRef(readyNotes);
	readyNotesRef.current = readyNotes;
	const knownIdsRef = useRef<Set<string> | null>(null);
	const pendingNoteIdRef = useRef<string | null>(null);

	const serverPending = generatingCount > 0 || pendingIds.length > 0;
	const finishReadyRef = useRef<(noteId: string) => void>(() => {});

	const wait = useGenerationWait({
		serverPending,
		readyEvent: "note:ready",
		matchReady: (raw) => {
			const payload = raw as {
				noteId?: string;
				materialId?: string | null;
				jobId?: string;
			};
			const id = payload.noteId ?? payload.jobId;
			if (!id) return false;
			const pending = pendingNoteIdRef.current;
			if (pending && (payload.noteId === pending || payload.jobId === pending)) return true;
			if (payload.materialId === materialId) return true;
			const known = knownIdsRef.current;
			return Boolean(known && !known.has(id));
		},
		failedEvent: "note:generation_failed",
		matchFailed: (raw) => {
			const payload = raw as { jobId?: string; materialId?: string | null };
			const pending = pendingNoteIdRef.current;
			if (pending) return !payload.jobId || payload.jobId === pending;
			if (!payload.jobId) return false;
			return itemsRef.current.some(
				(n) => n.id === payload.jobId && n.generation_status === "generating",
			);
		},
		onPoll: () => {
			invalidateRemote();
			const known = knownIdsRef.current;
			if (!known) return;
			const fresh = readyNotesRef.current.find((n) => !known.has(n.id));
			if (fresh) finishReadyRef.current(fresh.id);
		},
		pollIntervalMs: 3_000,
		timeoutMs: 120_000,
		onReady: (raw) => {
			const payload = raw as { noteId: string };
			finishReadyRef.current(payload.noteId);
		},
		onPendingCleared: () => {
			const known = knownIdsRef.current;
			if (!known) return;
			const fresh = readyNotesRef.current.find((n) => !known.has(n.id));
			if (fresh) finishReadyRef.current(fresh.id);
		},
		onFailed: (raw) => {
			knownIdsRef.current = null;
			pendingNoteIdRef.current = null;
			setPendingIds([]);
			const payload = raw as { errorMessage?: string };
			toast.error(t("workspace.genFailed", "Could not start generation"), {
				description: payload.errorMessage,
			});
		},
		onTimeout: () => {
			knownIdsRef.current = null;
			pendingNoteIdRef.current = null;
			setPendingIds([]);
			// Дедлайн ожидания ≠ провал: генерация идёт дальше, конспект появится в списке.
			toast.info(t("generate.toastSlow"));
		},
	});

	const finishReady = (noteId: string) => {
		knownIdsRef.current = null;
		pendingNoteIdRef.current = null;
		setPendingIds([]);
		wait.reset();
		void prefetchAndSeedNoteGetCache(qc, noteId)
			.catch(() => undefined)
			.then(() => navigate({ to: "/notes/$noteId", params: { noteId } }));
	};
	finishReadyRef.current = finishReady;

	// Overlay/PS может привезти ready раньше socket — не оставлять spinner.
	useEffect(() => {
		if (!wait.isWaiting) return;
		const known = knownIdsRef.current;
		if (!known) return;
		const fresh = readyNotes.find((n) => !known.has(n.id));
		if (fresh) finishReadyRef.current(fresh.id);
	}, [readyNotes, wait.isWaiting]);

	useEffect(() => {
		setPendingIds((prev) => {
			const next = prev.filter((id) => !items.some((n) => n.id === id));
			return next.length === prev.length ? prev : next;
		});
	}, [items]);

	function onGenerateQueued(jobId: string) {
		if (!materialId) return;
		knownIdsRef.current = new Set(items.map((n) => n.id));
		pendingNoteIdRef.current = jobId;
		setPendingIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
		wait.request();
	}

	const busy = wait.isWaiting || pendingIds.length > 0;

	return (
		<div className={cn(FORM_PAGE_SHELL, "flex flex-col")}>
			<nav
				aria-label="breadcrumb"
				className="flex items-center gap-2 text-sm text-muted-foreground"
			>
				<Link
					to="/materials/$materialId"
					params={{ materialId: materialId ?? "" }}
					className="inline-flex cursor-pointer items-center gap-1 transition-colors duration-fast hover:text-foreground"
				>
					<IconChevronLeft size={16} aria-hidden />
					{t("workspace.backToMaterial", "Back to material")}
				</Link>
				<span className="text-muted-foreground/40">/</span>
				<span className="font-medium text-foreground">{t("workspace.notesTab", "Notes")}</span>
			</nav>

			<SectionHeader
				category="ai"
				icon={<IconNotes />}
				title={t("workspace.notesTitle", "Material notes")}
				description={t("list.subtitle", { notes: readyNotes.length, materials: 1 })}
			/>

			<ElevatedPanel elevation="e1" className={PANEL_PAD}>
				<div className="mb-3 flex items-center gap-2">
					<IconSparkles size={16} aria-hidden className="text-primary" />
					<h3 className="text-sm font-semibold text-foreground">
						{t("workspace.generateTitle", "Generate AI note")}
					</h3>
				</div>
				<Button
					type="button"
					disabled={busy}
					onClick={() => setDialogOpen(true)}
					className="cursor-pointer gap-1.5"
				>
					<IconSparkles size={16} />
					{t("generate.submit")}
				</Button>
				{busy ? (
					<p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
						<IconLoader2 size={14} aria-hidden className="animate-spin text-primary" />
						{t("workspace.generatingHint")}
					</p>
				) : null}
			</ElevatedPanel>

			<GenerateNoteDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				initialMaterialId={materialId}
				lockMaterial
				onGenerateQueued={onGenerateQueued}
			/>

			<section className="flex flex-col gap-2">
				{isLoading ? (
					<>
						<Skeleton className="h-[60px] w-full rounded-[var(--radius-card)]" />
						<Skeleton className="h-[60px] w-full rounded-[var(--radius-card)]" />
					</>
				) : readyNotes.length === 0 && generatingCount === 0 && !busy ? (
					<div className="flex flex-col items-center justify-center gap-3 rounded-[var(--radius-panel)] border border-border bg-card py-14 text-center shadow-e1">
						<CategoryTile category="ai" size="xl" icon={<IconNotebook />} />
						<p className="max-w-sm text-sm text-muted-foreground">
							{t(
								"workspace.empty",
								"No notes for this material yet. Generate one or create it manually.",
							)}
						</p>
					</div>
				) : (
					<>
						{readyNotes.map((note) => (
							<Link
								key={note.id}
								to="/notes/$noteId"
								params={{ noteId: note.id }}
								className="group block rounded-[var(--radius-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							>
								<article className="flex items-center gap-3 rounded-[var(--radius-card)] border border-border bg-card p-3.5 shadow-e1 transition-[transform,box-shadow,border-color] duration-fast ease-[var(--ease-spring)] group-hover:-translate-y-[2px] group-hover:border-primary/35 group-hover:shadow-e3">
									<CategoryTile category="ai" size="md" icon={<IconNotebook />} />
									<div className="min-w-0 flex-1">
										<div className="flex items-start gap-2">
											<span className="line-clamp-2 min-w-0 text-sm font-semibold leading-snug text-foreground">
												{note.title}
											</span>
											<Badge variant="outline" className="mt-0.5 shrink-0 text-2xs tabular-nums">
												v{note.version}
											</Badge>
										</div>
									</div>
									<IconChevronRight
										size={16}
										aria-hidden
										className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
									/>
								</article>
							</Link>
						))}
						{(generatingCount > 0 || busy) && readyNotes.length === 0 && (
							<Skeleton className="h-[60px] w-full rounded-[var(--radius-card)]" />
						)}
					</>
				)}
			</section>
		</div>
	);
}
